import React from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { Flex, SvgIcon, Text } from 'Components/Base';
import { EXAM_PAPER_TYPE } from 'Context/ExamRoomContext/mapper';

import { CustomRadio } from '../ExamChoice';

// NOTE:
// This function is used to fix an issue where the HTMLView component
// does not correctly apply ellipsis styling (text-overflow: ellipsis)
// due to rendered HTML structure (e.g., nested <p> tags).
// It also strips HTML tags from the string to ensure plain text display
// with proper truncation.
//
// Important:
// - This approach cannot display images or any HTML elements.
// - It loses all formatting such as bold, italic, or links.
// - If the HTML content requires showing images or styling, this method is not suitable.
const stripHtml = (html) => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

const TextEllipsisWrapper = styled.div`
  font-weight: 700;
  letter-spacing: 0.1px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ExamResultsItemCard = styled(Flex)`
  background: white;
  padding: 16px;
  border-radius: 8px;
  flex-direction: ${(props) => (props.isTablet ? 'column' : 'row')};
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
`;

const TextItem = (props) => <Text textStyle={'body1'} fontWeight={700} {...props} />;

const ResultItemContent = ({
  itemType,
  articleTitle = 'Article Title',
  questionNo,
  questionIsCorrect,
}) => {
  const { t } = useTranslation('guidedPractice');

  if (itemType === EXAM_PAPER_TYPE.ARTICLE) {
    return (
      <>
        <SvgIcon
          icon={SvgIcon.ICON_LIST.BOOK_FILL}
          color={'primary'}
          style={{ minWidth: 24, minHeight: 24 }}
        />
        <TextEllipsisWrapper>{stripHtml(articleTitle)}</TextEllipsisWrapper>
      </>
    );
  }

  if (itemType === EXAM_PAPER_TYPE.QUESTION) {
    return (
      <>
        <TextItem>{`ข้อ.${questionNo}`}</TextItem>
        <CustomRadio className={questionIsCorrect ? 'correct' : 'incorrect'} />
        <TextItem>
          {questionIsCorrect
            ? t('EXAM_ROOM.RESULT.TEXT.CORRECT')
            : t('EXAM_ROOM.RESULT.TEXT.INCORRECT')}
        </TextItem>
      </>
    );
  }

  return null;
};

ResultItemContent.propTypes = {
  itemType: PropTypes.string.isRequired,
  articleTitle: PropTypes.string,
  questionNo: PropTypes.number,
  questionIsCorrect: PropTypes.bool,
};

const ExamResultsCard = ({
  item,
  onClick,
  questionNo = 0,
  isCorrect = false,
  isTablet = false,
  ...restProps
}) => {
  const handleClick = () => onClick(item);

  return (
    <ExamResultsItemCard isTablet={isTablet} onClick={handleClick} {...restProps}>
      <Flex
        alignItems={'center'}
        justifyContent={'space-between'}
        width={'100%'}
        flexGrow={1}
        gridColumnGap={2}
      >
        <Flex
          alignItems={'center'}
          gridColumnGap={6}
          flexGrow={1}
          style={{ whiteSpace: 'nowrap', overflow: 'hidden' }}
        >
          <ResultItemContent
            itemType={item.type}
            articleTitle={item.title}
            questionNo={questionNo}
            questionIsCorrect={isCorrect}
          />
        </Flex>
        <SvgIcon icon={SvgIcon.ICON_LIST.NEXT} style={{ minWidth: 24, minHeight: 24 }} />
      </Flex>
    </ExamResultsItemCard>
  );
};

ExamResultsCard.propTypes = {
  item: PropTypes.object.isRequired,
  onClick: PropTypes.func.isRequired,
  questionNo: PropTypes.number,
  isCorrect: PropTypes.bool,
  isTablet: PropTypes.bool,
};

export default ExamResultsCard;
